import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Warranty } from '@econtractor/types';
import { ProductTitle } from 'modules/product';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import PercentPie from 'components/PercentPie';
import WarrantyTableRowActions from './WarrantyTableRowActions';
import getWarrantyRemainingPercent from './model/getWarrantyRemainingPercent';
import PriceAndPriceSale from 'components/others/PriceAndPriceSale';
import type { WarrantyTableProps } from './types';

type WarrantyTableRowProps = WarrantyTableProps & { row: Warranty };

const WarrantyTableRow = ({ row, visible = {}, mini, hideActions, onOpen, onDetach, onDelete, onArchive, selected, onSelect }: WarrantyTableRowProps) => {
    const { t } = useTranslation('Warranties');
    return (
        <tr>
            {onSelect && (
                <td>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label={row.serialNumber}
                        checked={selected?.includes(row.ID) || false}
                        onChange={() => onSelect(row.ID)}
                    />
                </td>
            )}
            <td className="name">
                <ProductTitle
                    product={{
                        ID: row.productId,
                        prodName: row.prodName,
                        prodPhoto: row.prodPhoto || '',
                        prodStatus: row.prodStatus,
                        vendorId: row.vendorId,
                        vendorName: row.vendorName || '',
                    }}
                    onClick={() => (onOpen ? onOpen(row.ID) : onSelect?.(row.ID))}
                    onPhotoClick={() => (onOpen ? onOpen(row.ID) : onSelect?.(row.ID))}
                />
            </td>
            {!mini && visible.category && <td className="vendor">{row.categoryName || '—'}</td>}
            {!mini && visible.vendor && (
                <td className="vendor text-truncate" title={row.vendorName || ''}>
                    {row.vendorName || '—'}
                </td>
            )}
            {!mini && visible.id && (
                <td className="id">
                    <code>{row.productId}</code>
                </td>
            )}
            {!mini && visible.article && <td className="article">{row.prodArticle}</td>}
            <td className="serial-number text-truncate">
                <CopyTextWrapper value={row.serialNumber}>
                    <span title={row.serialNumber}>{row.serialNumber}</span>
                </CopyTextWrapper>
            </td>
            <td>{row.purchasedAt.split('-').reverse().join('.')}</td>
            <td
                className={
                    row.warrantyUntil < new Date().toLocaleDateString('sv-SE') ? 'text-danger' : ''
                }
            >
                <div>{row.warrantyUntil.split('-').reverse().join('.')}</div>
                <PercentPie
                    percent={getWarrantyRemainingPercent(row.purchasedAt, row.warrantyUntil)}
                    size="sm"
                />
            </td>
            {!mini && (
                <td className="warehouse">
                    {row.objectId ? (
                        <Link to={`/${row.onlySale ? 'sales' : 'objects'}/${row.objectId}`}>
                            {row.objectName}
                        </Link>
                    ) : (
                        <span className="text-muted">{t('unassigned')}</span>
                    )}
                </td>
            )}
            {!mini && visible.price && (
                <td>
                    <PriceAndPriceSale price={row.prodDefPrice} priceSale={row.prodDefPriceSale} />
                </td>
            )}
            {!hideActions && (
                <td className="action">
                    <WarrantyTableRowActions
                        id={row.ID}
                        onOpen={onOpen}
                        onDetach={onDetach}
                        onDelete={row.canEdit ? onDelete : undefined}
                        onArchive={row.canEdit && !row.objectId ? onArchive : undefined}
                        archived={Boolean(row.archived)}
                        assigned={Boolean(row.objectId)}
                    />
                </td>
            )}
        </tr>
    );
};

export default WarrantyTableRow;
